import { filters } from 'fabric';
import type { TInkTreatment } from '@/domain/print/TInkTreatment';
import type { FabricImage } from 'fabric';

// What a screen press does to a photograph, simulated with fabric's own filters: the image is
// flattened to its luminance, the split point is moved to where the customer put it, the
// transition is hardened, and the two ends are painted with the two inks.
//
// Four filters and no engine of our own. The alternative — analysing the pixels ourselves and
// rebuilding the bitmap — is the "moteur de traitement d'image" the specification describes and
// the kickoff cut, and it would have to run on every move of the slider.

// Just short of 1: a full hard threshold gives a stair-stepped edge on every curve, and a press
// lays ink through a screen, which is not stair-stepped either.
const EDGE_HARDNESS = 0.92;
function buildInkFilters(treatment: TInkTreatment): FabricImage['filters'] {
    const isSingleInk = treatment.colours.length < 2;
    const dark = _toChannels(treatment.colours[0]) ?? [0, 0, 0];
    const light = isSingleInk ? [255, 255, 255] : (_toChannels(treatment.colours[1]) ?? [255, 255, 255]);

    const chain = [
        new filters.Grayscale(),
        // Brightness shifts the luminance before the contrast bites, which is what moves the
        // split: the slider is a brightness offset wearing a more honest name.
        new filters.Brightness({ brightness: (0.5 - treatment.threshold) * 2 }),
        new filters.Contrast({ contrast: EDGE_HARDNESS }),
        new filters.ColorMatrix({ matrix: _duotoneMatrix(dark, light) }),
    ];
    if (!isSingleInk) {
        return chain;
    }
    // One ink means the light end is not printed at all, and a press cannot lay "nothing" as a
    // colour: it has to be a hole. Painting it with the cup's own colour looked right on a bare
    // cup and wrong the moment anything sat behind it — an opaque white square over a text.
    return [...chain, new filters.RemoveColor({ color: '#ffffff', distance: treatment.whiteDistance })];
}

// After the grayscale every channel carries the same luminance, so each output channel is one
// straight line from the dark ink at 0 to the light ink at 255. fabric reads the slope against
// the red channel and scales the offset by 255 (see ColorMatrix.applyTo2d).
function _duotoneMatrix(dark: number[], light: number[]): number[] {
    const [slopeRed, slopeGreen, slopeBlue] = dark.map((channel, index) => (light[index] - channel) / 255);
    const [offsetRed, offsetGreen, offsetBlue] = dark.map((channel) => channel / 255);
    return [slopeRed, 0, 0, 0, offsetRed, slopeGreen, 0, 0, 0, offsetGreen, slopeBlue, 0, 0, 0, offsetBlue, 0, 0, 0, 1, 0];
}

function _toChannels(colour: string | undefined): number[] | undefined {
    const match = colour === undefined ? null : /^#?([\da-f]{6})$/i.exec(colour.trim());
    if (!match) {
        return undefined;
    }
    const value = Number.parseInt(match[1], 16);
    return [(value >> 16) & 255, (value >> 8) & 255, value & 255];
}

export { buildInkFilters };
